/**
 * TutorPersonaSettings — redesigned for polish and better UX
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  Skeleton,
  Paper,
  Divider,
  Collapse,
  Grid,
} from '@mui/material';
import { CheckCircle, Psychology } from '@mui/icons-material';
import {
  TUTOR_PERSONAS,
  LEARNING_STYLES,
  getUserLearningProfile,
  updateTutorPersona,
  updateLearningStyle,
} from '../services/tutorPersonaService';
import logger from '../utils/logger';

// ── Example responses per persona ────────────────────────────────────────────

const PERSONA_EXAMPLES = {
  friendly_mentor:
    'Great question! Let\'s break this down together. Think of photosynthesis like a kitchen where plants cook their food using sunlight. The chloroplasts are like tiny solar panels. You\'re doing great — keep those questions coming! 🌱',
  strict_teacher:
    'Photosynthesis is the biochemical process by which plants convert light energy into chemical energy. The precise equation is: 6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂. Memorise this before proceeding to the light-dependent reactions.',
  socratic_guide:
    'Before I explain, tell me — what do plants need to survive? Where do you think they get their energy? If animals eat food, what do plants eat? Let\'s reason through this: what happens when you leave a plant in the dark for days?',
  buddy_peer:
    'Okay so photosynthesis is basically how plants make their own food, which is pretty cool ngl! Think of it like plants having tiny solar panels in their leaves — they\'re literally converting sunlight into energy, like nature\'s power bank! 🌿 Makes sense?',
  expert_professor:
    'Photosynthesis comprises two distinct phases: the light-dependent reactions in the thylakoid membranes and the Calvin cycle in the stroma. The quantum efficiency of photosystem II is particularly noteworthy — each absorbed photon contributes to the oxidation of water molecules…',
};

// ── Persona card ──────────────────────────────────────────────────────────────

function PersonaCard({ persona, selected, onSelect }) {
  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(persona.id)}
      sx={{
        p: 2, borderRadius: 2, cursor: 'pointer', position: 'relative',
        border: '1.5px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'background.paper',
        transition: 'all 0.15s',
        '&:hover': { borderColor: 'primary.main', bgcolor: selected ? 'primary.50' : 'action.hover' },
      }}
    >
      {selected && (
        <CheckCircle
          color="primary"
          fontSize="small"
          sx={{ position: 'absolute', top: 10, right: 10 }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="h5" lineHeight={1}>{persona.icon}</Typography>
        <Typography variant="subtitle2" fontWeight={700}>{persona.name}</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.55 }}>
        {persona.description}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {persona.traits.map((t, i) => (
          <Chip
            key={i}
            label={t}
            size="small"
            sx={{
              height: 20, fontSize: '0.72rem',
              bgcolor: selected ? 'primary.main' : 'action.selected',
              color: selected ? 'white' : 'text.secondary',
              fontWeight: 500,
            }}
          />
        ))}
      </Box>
    </Paper>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function TutorPersonaSettings({ user }) {
  const [loading,              setLoading]              = useState(true);
  const [saving,               setSaving]               = useState(false);
  const [selectedPersona,      setSelectedPersona]      = useState('friendly_mentor');
  const [selectedLearningStyle, setSelectedLearningStyle] = useState(null);
  const [currentProfile,       setCurrentProfile]       = useState(null);
  const [hasChanges,           setHasChanges]           = useState(false);
  const [saveStatus,           setSaveStatus]           = useState(null); // 'success' | 'error'

  useEffect(() => { loadProfile(); }, [user]);

  async function loadProfile() {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const profile = await getUserLearningProfile(user.uid);
      setCurrentProfile(profile);
      setSelectedPersona(profile.personaId || 'friendly_mentor');
      setSelectedLearningStyle(profile.learningStyleId || null);
      setHasChanges(false);
    } catch (err) {
      logger.error('Error loading learning profile:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!user) { setSaveStatus('error'); return; }
    try {
      setSaving(true);
      setSaveStatus(null);
      if (selectedPersona !== currentProfile?.personaId)
        await updateTutorPersona(user.uid, selectedPersona);
      if (selectedLearningStyle !== currentProfile?.learningStyleId)
        await updateLearningStyle(user.uid, selectedLearningStyle);
      setSaveStatus('success');
      setHasChanges(false);
      await loadProfile();
    } catch (err) {
      logger.error('Error saving preferences:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 4000);
    }
  }

  // ── Loading skeleton ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={110} sx={{ borderRadius: 2 }} />)}
      </Box>
    );
  }

  const personas     = Object.values(TUTOR_PERSONAS);
  const learningStyles = Object.values(LEARNING_STYLES);
  const exampleText  = PERSONA_EXAMPLES[selectedPersona] || PERSONA_EXAMPLES.friendly_mentor;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {!user && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Sign in to save your tutor preferences across devices.
        </Alert>
      )}

      {/* Persona selection */}
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.72rem' }}>
        Choose Your Tutor Personality
      </Typography>

      <Grid container spacing={1.5}>
        {personas.map(persona => (
          <Grid item xs={12} sm={6} key={persona.id}>
            <PersonaCard
              persona={persona}
              selected={selectedPersona === persona.id}
              onSelect={(id) => { setSelectedPersona(id); setHasChanges(true); }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Live preview */}
      <Collapse in={!!selectedPersona}>
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', borderStyle: 'dashed' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <Psychology fontSize="small" color="primary" />
            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Preview: How your tutor responds
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, fontStyle: 'italic' }}>
            "{exampleText}"
          </Typography>
        </Paper>
      </Collapse>

      <Divider />

      {/* Learning style */}
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.72rem' }}>
        Learning Style <Chip label="Optional" size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />
      </Typography>

      <RadioGroup
        value={selectedLearningStyle || ''}
        onChange={(e) => { setSelectedLearningStyle(e.target.value); setHasChanges(true); }}
      >
        <Grid container spacing={1}>
          {learningStyles.map(style => {
            const active = selectedLearningStyle === style.id;
            return (
              <Grid item xs={12} sm={6} key={style.id}>
                <Paper
                  variant="outlined"
                  onClick={() => { setSelectedLearningStyle(style.id); setHasChanges(true); }}
                  sx={{
                    p: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'primary.50' : 'background.paper',
                    '&:hover': { borderColor: 'primary.main', bgcolor: active ? 'primary.50' : 'action.hover' },
                    transition: 'all 0.15s',
                  }}
                >
                  <FormControlLabel
                    value={style.id}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                          <Typography variant="body2">{style.icon}</Typography>
                          <Typography variant="body2" fontWeight={600}>{style.name}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                          {style.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </RadioGroup>

      {/* Save / status */}
      {saveStatus === 'success' && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Preferences saved! Ekamanam will use the new personality from now on.
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Failed to save. Please check your connection and try again.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={loadProfile}
          disabled={!hasChanges || saving}
          sx={{ borderRadius: 2 }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          sx={{ borderRadius: 2, px: 3, minWidth: 130 }}
        >
          {saving ? 'Saving…' : 'Save Preferences'}
        </Button>
      </Box>
    </Box>
  );
}

export default TutorPersonaSettings;
